import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, useInView } from 'framer-motion';
// Animated Counter Component
const AnimatedCounter: React.FC<{
  end: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
}> = ({
  end,
  duration = 2,
  suffix = '',
  prefix = '',
  decimals = 0
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!isInView) return;
    let startTime: number;
    let animationFrame: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = (timestamp - startTime) / (duration * 1000);
      if (progress < 1) {
        const currentValue = end * progress;
        setCount(decimals > 0 ? parseFloat(currentValue.toFixed(decimals)) : Math.floor(currentValue));
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isInView, end, duration, decimals]);
  return <span ref={ref} data-id="element-1814">
      {prefix}
      {count}
      {suffix}
    </span>;
};
// Logo Component
const Logo: React.FC = () => <motion.a href="/" initial={{
  opacity: 0,
  y: -20
}} animate={{
  opacity: 1,
  y: 0
}} transition={{
  duration: 0.6
}} className="absolute top-4 right-4 md:top-8 md:right-8 z-50 group" data-id="element-1815">
    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nrvkiu6sNi7Rupzm8hWkWo/yellow_full_logo.png" alt="HIRE" className="h-6 md:h-8 w-auto transition-transform group-hover:scale-105" data-id="element-1816" />
  </motion.a>;
// Page Container Component
const Page: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({
  children,
  className = ''
}) => <motion.div initial={{
  opacity: 0
}} whileInView={{
  opacity: 1
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6
}} className={`min-h-screen w-full flex flex-col relative ${className}`} data-id="element-1817">
    <Logo data-id="element-1818" />
    {children}
  </motion.div>;
// Data Box Component
const DataBox: React.FC<{
  label: string;
  value: string;
}> = ({
  label,
  value
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.5
}} className="border-l-4 border-amber pl-4 py-2" data-id="element-1819">
    <div className="text-slate-400 text-sm font-medium uppercase tracking-wider" data-id="element-1820">
      {label}
    </div>
    <div className="text-white text-lg font-semibold mt-1" data-id="element-1821">{value}</div>
  </motion.div>;
// Problem Item Component
const ProblemItem: React.FC<{
  text: string;
  index: number;
}> = ({
  text,
  index
}) => <motion.div initial={{
  opacity: 0,
  x: -20
}} whileInView={{
  opacity: 1,
  x: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.5,
  delay: index * 0.1
}} className="flex items-start gap-3 mb-4" data-id="element-1822">
    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center mt-1" data-id="element-1823">
      <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1824">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-1825" />
      </svg>
    </div>
    <div className="text-slate-300 leading-relaxed" data-id="element-1826">{text}</div>
  </motion.div>;
// Process Step Component
const ProcessStep: React.FC<{
  number: string;
  title: string;
  description: string;
  index: number;
}> = ({
  number,
  title,
  description,
  index
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.6,
  delay: index * 0.15
}} className="relative" data-id="element-1827">
    <div className="flex items-start gap-4" data-id="element-1828">
      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-amber flex items-center justify-center" data-id="element-1829">
        <span className="text-navy text-xl font-bold" data-id="element-1830">{number}</span>
      </div>
      <div className="flex-1" data-id="element-1831">
        <h3 className="text-white text-xl font-bold mb-2" data-id="element-1832">{title}</h3>
        <p className="text-slate-300 leading-relaxed" data-id="element-1833">{description}</p>
      </div>
    </div>
  </motion.div>;
// Stat Card Component
const StatCard: React.FC<{
  value: string;
  label: string;
  index: number;
  highlight?: boolean;
}> = ({
  value,
  label,
  index,
  highlight = false
}) => <motion.div initial={{
  opacity: 0,
  scale: 0.9
}} whileInView={{
  opacity: 1,
  scale: 1
}} viewport={{
  once: true
}} transition={{
  duration: 0.5,
  delay: index * 0.1
}} className={`p-6 rounded-lg ${highlight ? 'bg-amber' : 'bg-white'} border-2 ${highlight ? 'border-amber-dark' : 'border-slate-200'}`} data-id="element-1834">
    <div className={`text-4xl font-bold mb-2 ${highlight ? 'text-navy' : 'text-amber'}`} data-id="element-1835">
      {value}
    </div>
    <div className={`text-sm font-medium uppercase tracking-wider ${highlight ? 'text-navy-light' : 'text-slate-600'}`} data-id="element-1836">
      {label}
    </div>
  </motion.div>;
export function WindowsCaseStudy() {
  return <div className="w-full bg-slate-50 font-sans" data-id="element-1837">
      {/* PAGE 1: COVER */}
      <Page className="bg-navy items-center justify-center text-center px-8" data-id="element-1838">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="max-w-4xl mx-auto" data-id="element-1839">
          <div className="mb-12" data-id="element-1840">
            <div className="text-amber text-sm font-bold uppercase tracking-widest mb-4" data-id="element-1841">
              The Operator's Playbook
            </div>
            <h1 className="text-6xl md:text-7xl font-bold text-white mb-4 leading-tight" data-id="element-1842">
              The HIRE Advantage
            </h1>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="mb-8" data-id="element-1843">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight" data-id="element-1844">
              CASE STUDY:
              <br data-id="element-1845" />
              The 5-Market Expansion Playbook
            </h2>
            <p className="text-xl md:text-2xl text-amber font-semibold leading-relaxed" data-id="element-1846">
              How We Recruited 39 Reps in 30 Days, Launched 3 New Markets, and
              Supported Over 250 Full-Commission Offers
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 1
        }} className="text-slate-400 text-lg" data-id="element-1847">
            A Field Report for D2D Operators
          </motion.div>
        </motion.div>
      </Page>

      {/* PAGE 2: COMPANY SNAPSHOT */}
      <Page className="bg-slate-50 px-8 py-16" data-id="element-1848">
        <div className="max-w-6xl mx-auto" data-id="element-1849">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12" data-id="element-1850">
            <h2 className="text-4xl font-bold text-navy mb-4" data-id="element-1851">
              Company Snapshot
            </h2>
            <div className="w-20 h-1 bg-amber mb-6" data-id="element-1852"></div>
            <p className="text-slate-700 text-xl leading-relaxed max-w-4xl" data-id="element-1853">
              This Colorado-based residential home-services company had a strong
              leadership foundation, a proven product, and a vision for
              multi-market expansion. But like many fast-growing organizations,
              they lacked a fully developed recruiting engine to support
              sustained scale.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 mb-12" data-id="element-1854">
            {/* Left Column: Snapshot Table */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-navy p-8 rounded-lg" data-id="element-1855">
              <h3 className="text-amber text-xl font-bold mb-6 uppercase tracking-wider" data-id="element-1856">
                Snapshot Overview
              </h3>
              <div className="space-y-6" data-id="element-1857">
                <DataBox label="Industry" value="Residential Window Replacement" data-id="element-1858" />
                <DataBox label="Location" value="Colorado → Mountain West" data-id="element-1859" />
                <DataBox label="Team Size (Before)" value="Strong veteran core + inconsistent rookie flow" data-id="element-1860" />
                <DataBox label="Team Size (After)" value="5 markets with consistent weekly hires" data-id="element-1861" />
                <DataBox label="Primary Service" value="In-home window replacement" data-id="element-1862" />
                <DataBox label="Markets" value="Colorado Springs → Denver → Spokane → Tucson → Phoenix" data-id="element-1863" />
              </div>
            </motion.div>

            {/* Right Column: Context */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-1864">
              <h3 className="text-navy text-2xl font-bold mb-6" data-id="element-1865">
                The Starting Point
              </h3>
              <div className="space-y-4 text-slate-700 leading-relaxed" data-id="element-1866">
                <p data-id="element-1867">
                  They entered the partnership already operating two markets —
                  Colorado Springs and Denver — and wanted to expand even
                  faster.
                </p>
                <p data-id="element-1868">To do that, they needed:</p>
                <ul className="space-y-2 ml-6" data-id="element-1869">
                  <li className="flex items-start gap-2" data-id="element-1870">
                    <span className="text-amber mt-1" data-id="element-1871">•</span>
                    <span data-id="element-1872">Consistent rookie flow</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1873">
                    <span className="text-amber mt-1" data-id="element-1874">•</span>
                    <span data-id="element-1875">A stronger pipeline process</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1876">
                    <span className="text-amber mt-1" data-id="element-1877">•</span>
                    <span data-id="element-1878">
                      A system that could scale into additional territories
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1879">
                    <span className="text-amber mt-1" data-id="element-1880">•</span>
                    <span data-id="element-1881">
                      Without overwhelming leadership or distracting from
                      production and culture
                    </span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-amber/10 border-l-4 border-amber p-6 rounded-r-lg" data-id="element-1882">
            <p className="text-navy text-lg font-semibold" data-id="element-1883">
              The opportunity was enormous. They simply needed a scalable system
              to match it.
            </p>
          </motion.div>
        </div>
      </Page>

      {/* PAGE 3: THE "BEFORE" STATE */}
      <Page className="bg-navy px-8 py-16" data-id="element-1884">
        <div className="max-w-6xl mx-auto" data-id="element-1885">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12" data-id="element-1886">
            <h2 className="text-4xl font-bold text-white mb-4" data-id="element-1887">
              Starting Point: Their "Before" State
            </h2>
            <div className="w-20 h-1 bg-amber mb-6" data-id="element-1888"></div>
            <p className="text-slate-300 text-xl leading-relaxed max-w-4xl" data-id="element-1889">
              When we first met, the company wasn't struggling from lack of
              opportunity — they were struggling from underdeveloped recruiting
              infrastructure that capped their growth potential.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 mb-12" data-id="element-1890">
            <div data-id="element-1891">
              <h3 className="text-amber text-2xl font-bold mb-6" data-id="element-1892">
                Core Hiring Problems
              </h3>
              <div className="space-y-1" data-id="element-1893">
                <ProblemItem index={0} text="Underdeveloped Infrastructure: Lightly structured, largely manual, dependent on leadership time, vulnerable to inconsistency. They were generating applicants but lacked reliable filtering, qualifying, scheduling, warming, and integration." data-id="element-1894" />
                <ProblemItem index={1} text="Leadership Bottleneck: Managers stretched thin across recruiting, training, onboarding, daily support, personal production, and market expansion. Doing everything right — just too much manually." data-id="element-1895" />
                <ProblemItem index={2} text="Early-Stage Drop-Off: Without structured pre-launch warmup, scripted integration, predictable accountability, and consistent touchpoints, many rookies who should have succeeded didn't make it to Day 1." data-id="element-1896" />
                <ProblemItem index={3} text="Inconsistent Rookie Flow: The struggle wasn't hiring forever — it was consistency. Without weekly momentum, veterans plateau, leadership loses bandwidth, new markets get delayed, and revenue becomes inconsistent." data-id="element-1897" />
              </div>
            </div>

            <div data-id="element-1898">
              <h3 className="text-amber text-2xl font-bold mb-6" data-id="element-1899">
                Previous Strategy
              </h3>
              <div className="bg-slate-800 p-6 rounded-lg mb-6" data-id="element-1900">
                <p className="text-slate-300 leading-relaxed mb-4" data-id="element-1901">
                  Before partnering, their process included:
                </p>
                <ul className="space-y-3 text-slate-300" data-id="element-1902">
                  <li className="flex items-start gap-2" data-id="element-1903">
                    <span className="text-red-400 mt-1" data-id="element-1904">×</span>
                    <span data-id="element-1905">Basic job postings</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1906">
                    <span className="text-red-400 mt-1" data-id="element-1907">×</span>
                    <span data-id="element-1908">Manual interview scheduling</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1909">
                    <span className="text-red-400 mt-1" data-id="element-1910">×</span>
                    <span data-id="element-1911">Gut-feeling qualification</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1912">
                    <span className="text-red-400 mt-1" data-id="element-1913">×</span>
                    <span data-id="element-1914">Limited pre-training</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1915">
                    <span className="text-red-400 mt-1" data-id="element-1916">×</span>
                    <span data-id="element-1917">Inconsistent follow-up</span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber/10 border-l-4 border-amber p-6 rounded-r-lg" data-id="element-1918">
                <p className="text-white font-semibold mb-2" data-id="element-1919">
                  The Operational Cost
                </p>
                <p className="text-slate-300 leading-relaxed" data-id="element-1920">
                  Leaders were spread too thin. Recruiting felt reactive. Growth
                  opportunities were slipping. Markets were stalling due to lack
                  of rookies. The pain wasn't hiring — the pain was not having a
                  system.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Page>

      {/* PAGE 4: GOALS */}
      <Page className="bg-slate-50 px-8 py-16" data-id="element-1921">
        <div className="max-w-6xl mx-auto" data-id="element-1922">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12 text-center" data-id="element-1923">
            <h2 className="text-4xl font-bold text-navy mb-4" data-id="element-1924">
              Goals: What We Set Out to Solve
            </h2>
            <div className="w-20 h-1 bg-amber mx-auto mb-6" data-id="element-1925"></div>
            <p className="text-slate-600 text-xl max-w-3xl mx-auto" data-id="element-1926">
              The goals were measurable, strategic, and aligned with their
              long-term expansion plan.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-1927">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-white p-8 rounded-lg border-2 border-slate-200" data-id="element-1928">
              <div className="w-12 h-12 bg-amber rounded-lg flex items-center justify-center mb-4" data-id="element-1929">
                <span className="text-navy text-2xl font-bold" data-id="element-1930">1</span>
              </div>
              <h3 className="text-navy text-xl font-bold mb-3" data-id="element-1931">
                Build a Predictable Weekly Recruiting Engine
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-1932">
                A system that generates quality applicants, screens them
                quickly, moves them through the pipeline, and produces
                consistent weekly hires. Week in, week out.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="bg-white p-8 rounded-lg border-2 border-slate-200" data-id="element-1933">
              <div className="w-12 h-12 bg-amber rounded-lg flex items-center justify-center mb-4" data-id="element-1934">
                <span className="text-navy text-2xl font-bold" data-id="element-1935">2</span>
              </div>
              <h3 className="text-navy text-xl font-bold mb-3" data-id="element-1936">
                Strengthen Qualification & Screening
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-1937">
                Using targeted ad buying, sales-driven copy, optimized job
                placement, application filtering, multi-layer screening, fast
                scheduling, aggressive follow-up, and interview show-rate
                optimization.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="bg-white p-8 rounded-lg border-2 border-slate-200" data-id="element-1938">
              <div className="w-12 h-12 bg-amber rounded-lg flex items-center justify-center mb-4" data-id="element-1939">
                <span className="text-navy text-2xl font-bold" data-id="element-1940">3</span>
              </div>
              <h3 className="text-navy text-xl font-bold mb-3" data-id="element-1941">
                Improve Early-Stage Commitment & Integration
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-1942">
                With a pre-launch warmup framework, script test, accountability
                structure, clear expectations, and multi-day touchpoint rhythm.
                This dramatically increases both show-up and early retention.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="bg-white p-8 rounded-lg border-2 border-slate-200" data-id="element-1943">
              <div className="w-12 h-12 bg-amber rounded-lg flex items-center justify-center mb-4" data-id="element-1944">
                <span className="text-navy text-2xl font-bold" data-id="element-1945">4</span>
              </div>
              <h3 className="text-navy text-xl font-bold mb-3" data-id="element-1946">
                Enable Multi-Market Expansion
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-1947">
                Their leadership wanted to scale quickly and intelligently. The
                recruiting engine needed to support opening Spokane, Tucson, and
                Phoenix without sacrificing quality or burning out their team.
              </p>
            </motion.div>
          </div>
        </div>
      </Page>

      {/* PAGE 5: THE SOLUTION */}
      <Page className="bg-navy px-8 py-16" data-id="element-1948">
        <div className="max-w-6xl mx-auto" data-id="element-1949">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12 text-center" data-id="element-1950">
            <h2 className="text-4xl font-bold text-white mb-4" data-id="element-1951">
              What We Built: The HIRE Advantage
            </h2>
            <div className="w-20 h-1 bg-amber mx-auto mb-6" data-id="element-1952"></div>
            <p className="text-slate-300 text-xl max-w-3xl mx-auto" data-id="element-1953">
              We implemented the proven HIRE recruiting engine — the same system
              we use to help companies scale consistently and sustainably.
            </p>
          </motion.div>

          <div className="grid gap-8 mb-12" data-id="element-1954">
            <ProcessStep index={0} number="01" title="Conversion-Optimized Strategy" description="High-performance ad copy and strategic job placement designed to attract quality candidates at scale." data-id="element-1955" />
            <ProcessStep index={1} number="02" title="The Filter" description="Multi-layer screening and application filters to remove low-intent candidates before they consume leadership time." data-id="element-1956" />
            <ProcessStep index={2} number="03" title="The Automation" description="Automated scheduling and aggressive show-rate support to eliminate the ghosting issue and maximize interview conversion." data-id="element-1957" />
            <ProcessStep index={3} number="04" title="The Launch" description="Integrated onboarding and sales-school readiness processes to ensure rookies show up prepared and committed." data-id="element-1958" />
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-slate-800 p-8 rounded-lg border-2 border-amber" data-id="element-1959">
            <p className="text-white text-lg text-center font-medium" data-id="element-1960">
              No custom software. No special exceptions.
              <br data-id="element-1961" />
              <span className="text-amber font-bold" data-id="element-1962">
                Just a world-class system executed well.
              </span>
            </p>
          </motion.div>
        </div>
      </Page>

      {/* PAGE 6: THE RESULTS */}
      <Page className="bg-slate-50 px-8 py-16" data-id="element-1963">
        <div className="max-w-6xl mx-auto" data-id="element-1964">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12 text-center" data-id="element-1965">
            <h2 className="text-5xl font-bold text-navy mb-4" data-id="element-1966">The Results</h2>
            <div className="w-20 h-1 bg-amber mx-auto mb-6" data-id="element-1967"></div>
            <p className="text-slate-600 text-xl" data-id="element-1968">
              The performance gains were immediate and sustained
            </p>
          </motion.div>

          {/* Hero Stats */}
          <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-1969">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-amber p-12 rounded-lg text-center" data-id="element-1970">
              <div className="text-7xl font-bold text-navy mb-4" data-id="element-1971">
                <AnimatedCounter end={39} data-id="element-1972" />
              </div>
              <div className="text-navy text-2xl font-bold uppercase tracking-wider" data-id="element-1973">
                New Hires
              </div>
              <div className="text-navy-light text-lg mt-2" data-id="element-1974">in 30 Days</div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="bg-navy p-12 rounded-lg text-center" data-id="element-1975">
              <div className="text-7xl font-bold text-amber mb-4" data-id="element-1976">
                <AnimatedCounter end={250} suffix="+" data-id="element-1977" />
              </div>
              <div className="text-white text-2xl font-bold uppercase tracking-wider" data-id="element-1978">
                Total Offers
              </div>
              <div className="text-slate-400 text-lg mt-2" data-id="element-1979">
                Across 5 Markets
              </div>
            </motion.div>
          </div>

          {/* Detailed Stats Grid */}
          <div className="grid md:grid-cols-4 gap-6 mb-12" data-id="element-1980">
            <StatCard index={0} value="13" label="Hires in First 10 Days" data-id="element-1981" />
            <StatCard index={1} value="74%" label="Conversion Rate" highlight data-id="element-1982" />
            <StatCard index={2} value="50%+" label="Show Rate" data-id="element-1983" />
            <StatCard index={3} value="$1.2M+" label="Spokane Revenue" highlight data-id="element-1984" />
          </div>

          {/* Timeline */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-white p-8 rounded-lg border-2 border-slate-200 mb-12" data-id="element-1985">
            <h3 className="text-2xl font-bold text-navy mb-6" data-id="element-1986">
              Expansion Timeline
            </h3>
            <div className="space-y-6" data-id="element-1987">
              <div className="flex items-start gap-4" data-id="element-1988">
                <div className="flex-shrink-0 w-32 text-amber font-bold" data-id="element-1989">
                  First 10 Days
                </div>
                <div className="flex-1 text-slate-700" data-id="element-1990">
                  <span className="font-semibold text-navy" data-id="element-1991">13 Hires</span> made
                  immediately with 50%+ show rates. Managers freed to focus on
                  leadership, not logistics.
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-1992">
                <div className="flex-shrink-0 w-32 text-amber font-bold" data-id="element-1993">
                  First 30 Days
                </div>
                <div className="flex-1 text-slate-700" data-id="element-1994">
                  <span className="font-semibold text-navy" data-id="element-1995">
                    53 interviews → 39 hires
                  </span>
                  . System efficiency dramatically increased. Rookie flow
                  stabilized. Leaders gained bandwidth. Expansion planning
                  accelerated.
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-1996">
                <div className="flex-shrink-0 w-32 text-amber font-bold" data-id="element-1997">
                  Market Expansion
                </div>
                <div className="flex-1 text-slate-700" data-id="element-1998">
                  Launched{' '}
                  <span className="font-semibold text-navy" data-id="element-1999">
                    Spokane, Tucson, and Phoenix
                  </span>
                  . Spokane quickly became their top-producing branch,
                  generating over $1.2M in revenue within the first few months.
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2000">
                <div className="flex-shrink-0 w-32 text-amber font-bold" data-id="element-2001">
                  First 60-75 Days
                </div>
                <div className="flex-1 text-slate-700" data-id="element-2002">
                  Revenue generated by rookies created{' '}
                  <span className="font-semibold text-navy" data-id="element-2003">
                    break-even ROI in just over 2 months
                  </span>
                  . From that point onward, the recruiting engine functionally
                  built and scaled the team at net zero cost.
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2004">
                <div className="flex-shrink-0 w-32 text-amber font-bold" data-id="element-2005">
                  To Date
                </div>
                <div className="flex-1 text-slate-700" data-id="element-2006">
                  <span className="font-semibold text-navy" data-id="element-2007">
                    250+ Full-Commission Offers
                  </span>{' '}
                  across five markets. In full-commission D2D, you scale by
                  keeping top performers and consistently generating strong
                  rookie flow.
                </div>
              </div>
            </div>
          </motion.div>

          {/* Key Insight */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-navy p-8 rounded-lg text-center" data-id="element-2008">
            <p className="text-white text-xl leading-relaxed" data-id="element-2009">
              <span className="text-amber font-bold" data-id="element-2010">This pace is rare</span> in
              full-commission D2D environments — but absolutely achievable with
              the right system.
            </p>
          </motion.div>
        </div>
      </Page>

      {/* PAGE 7: THE PARTNERSHIP */}
      <Page className="bg-navy px-8 py-16" data-id="element-2011">
        <div className="max-w-6xl mx-auto" data-id="element-2012">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12" data-id="element-2013">
            <h2 className="text-4xl font-bold text-white mb-4" data-id="element-2014">
              The True Growth Partnership
            </h2>
            <div className="w-20 h-1 bg-amber mb-6" data-id="element-2015"></div>
            <p className="text-slate-300 text-xl" data-id="element-2016">
              We didn't just build the engine and leave. We partnered deeply
              with leadership to ensure the system stayed powerful.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-2017">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-slate-800 p-8 rounded-lg border-l-4 border-amber" data-id="element-2018">
              <h3 className="text-amber text-xl font-bold mb-4 uppercase tracking-wider" data-id="element-2019">
                Our Role
              </h3>
              <ul className="space-y-3 text-slate-300" data-id="element-2020">
                <li className="flex items-start gap-2" data-id="element-2021">
                  <span className="text-amber mt-1" data-id="element-2022">✓</span>
                  <span data-id="element-2023">Upskilled managers</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2024">
                  <span className="text-amber mt-1" data-id="element-2025">✓</span>
                  <span data-id="element-2026">Supported their full-time recruiter</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2027">
                  <span className="text-amber mt-1" data-id="element-2028">✓</span>
                  <span data-id="element-2029">Trained leaders on rookie integration</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2030">
                  <span className="text-amber mt-1" data-id="element-2031">✓</span>
                  <span data-id="element-2032">Reinforced a culture of recruiting</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2033">
                  <span className="text-amber mt-1" data-id="element-2034">✓</span>
                  <span data-id="element-2035">Strengthened accountability rhythms</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2036">
                  <span className="text-amber mt-1" data-id="element-2037">✓</span>
                  <span data-id="element-2038">
                    Built long-term recruiting behavior into their DNA
                  </span>
                </li>
              </ul>
              <p className="text-slate-300 leading-relaxed mt-6 pt-6 border-t border-slate-700" data-id="element-2039">
                Our job is to build and maintain the machine.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-slate-800 p-8 rounded-lg border-l-4 border-amber" data-id="element-2040">
              <h3 className="text-amber text-xl font-bold mb-4 uppercase tracking-wider" data-id="element-2041">
                Their Role
              </h3>
              <ul className="space-y-3 text-slate-300" data-id="element-2042">
                <li className="flex items-start gap-2" data-id="element-2043">
                  <span className="text-amber mt-1" data-id="element-2044">✓</span>
                  <span data-id="element-2045">Lead</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2046">
                  <span className="text-amber mt-1" data-id="element-2047">✓</span>
                  <span data-id="element-2048">Train</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2049">
                  <span className="text-amber mt-1" data-id="element-2050">✓</span>
                  <span data-id="element-2051">Develop</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2052">
                  <span className="text-amber mt-1" data-id="element-2053">✓</span>
                  <span data-id="element-2054">Support</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2055">
                  <span className="text-amber mt-1" data-id="element-2056">✓</span>
                  <span data-id="element-2057">Execute at a high level</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2058">
                  <span className="text-amber mt-1" data-id="element-2059">✓</span>
                  <span data-id="element-2060">Build culture</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2061">
                  <span className="text-amber mt-1" data-id="element-2062">✓</span>
                  <span data-id="element-2063">Drive performance</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2064">
                  <span className="text-amber mt-1" data-id="element-2065">✓</span>
                  <span data-id="element-2066">Grow markets</span>
                </li>
              </ul>
              <p className="text-slate-300 leading-relaxed mt-6 pt-6 border-t border-slate-700" data-id="element-2067">
                Their leadership team did exactly what they do best.
              </p>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-amber p-10 rounded-lg text-center" data-id="element-2068">
            <h3 className="text-navy text-3xl font-bold mb-6" data-id="element-2069">The ROI</h3>
            <div className="grid md:grid-cols-2 gap-8 text-left" data-id="element-2070">
              <div data-id="element-2071">
                <div className="text-navy font-bold text-lg mb-2" data-id="element-2072">
                  Self-Funding in 60-75 Days
                </div>
                <div className="text-navy-light" data-id="element-2073">
                  Revenue generated by rookies created break-even ROI in just
                  over 2 months. Although install timelines extended the cash
                  cycle, the system itself became self-funding faster than
                  projected.
                </div>
              </div>
              <div data-id="element-2074">
                <div className="text-navy font-bold text-lg mb-2" data-id="element-2075">
                  Net Zero Cost at Scale
                </div>
                <div className="text-navy-light" data-id="element-2076">
                  From that point forward, the recruiting engine functionally
                  built and scaled the team at net zero cost. Together, the
                  partnership created multiple seven figures in revenue and a
                  blueprint for continued expansion.
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Page>

      {/* PAGE 8: SUMMARY & CTA */}
      <Page className="bg-slate-50 px-8 py-16" data-id="element-2077">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2078">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12" data-id="element-2079">
            <h2 className="text-5xl font-bold text-navy mb-4" data-id="element-2080">Final Summary</h2>
            <div className="w-20 h-1 bg-amber mx-auto mb-6" data-id="element-2081"></div>
            <p className="text-slate-700 text-xl leading-relaxed" data-id="element-2082">
              This case study is a clear example of what happens when a strong
              company, a committed leadership team, and the right recruiting
              engine come together with alignment and clarity.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="mb-12" data-id="element-2083">
            <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-2084">
              <div className="bg-navy p-6 rounded-lg" data-id="element-2085">
                <div className="text-4xl font-bold text-amber mb-2" data-id="element-2086">39</div>
                <div className="text-white text-sm" data-id="element-2087">Hires in 30 Days</div>
              </div>
              <div className="bg-navy p-6 rounded-lg" data-id="element-2088">
                <div className="text-4xl font-bold text-amber mb-2" data-id="element-2089">13</div>
                <div className="text-white text-sm" data-id="element-2090">Hires in First 10 Days</div>
              </div>
              <div className="bg-navy p-6 rounded-lg" data-id="element-2091">
                <div className="text-4xl font-bold text-amber mb-2" data-id="element-2092">74%</div>
                <div className="text-white text-sm" data-id="element-2093">
                  Interview Conversion Rate
                </div>
              </div>
              <div className="bg-navy p-6 rounded-lg" data-id="element-2094">
                <div className="text-4xl font-bold text-amber mb-2" data-id="element-2095">3</div>
                <div className="text-white text-sm" data-id="element-2096">New Markets Launched</div>
              </div>
              <div className="bg-navy p-6 rounded-lg" data-id="element-2097">
                <div className="text-4xl font-bold text-amber mb-2" data-id="element-2098">$1.2M+</div>
                <div className="text-white text-sm" data-id="element-2099">Spokane Market Revenue</div>
              </div>
              <div className="bg-navy p-6 rounded-lg" data-id="element-2100">
                <div className="text-4xl font-bold text-amber mb-2" data-id="element-2101">250+</div>
                <div className="text-white text-sm" data-id="element-2102">
                  Total Full-Commission Offers
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="bg-navy p-12 rounded-lg mb-8" data-id="element-2103">
            <p className="text-white text-2xl leading-relaxed mb-6" data-id="element-2104">
              This case study proves what happens when a strong company meets
              the right recruiting engine.
            </p>
            <p className="text-amber text-xl font-bold" data-id="element-2105">
              Give leaders a predictable recruiting engine, and they can scale
              into any market they choose.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="bg-amber p-10 rounded-lg" data-id="element-2106">
            <h3 className="text-navy text-2xl font-bold mb-4" data-id="element-2107">
              Ready to Install Your System?
            </h3>
            <p className="text-navy-light text-lg mb-6" data-id="element-2108">
              Schedule a Strategy Call and discover how The HIRE Advantage can
              transform your recruiting infrastructure.
            </p>
            <a href="/book-call" className="inline-block bg-navy hover:bg-navy-light text-white font-bold px-8 py-4 rounded-lg transition-colors text-lg" data-id="element-2109">
              Schedule Your Strategy Call →
            </a>
          </motion.div>
        </div>
      </Page>
    </div>;
}